import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, Save, Play, X, ChevronDown, Shuffle, SkipForward, SkipBack } from "lucide-react";
import AudioUploadButton from "@/components/AudioUploadButton";
import ImageUploadButton from "@/components/ImageUploadButton";
import AdminTrackRouter from "@/components/AdminTrackRouter";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyShow = { title: "", dj: "", day: "Mon", start_time: "18:00", end_time: "20:00", description: "", cover_url: "", genre: "" };
const emptyTrack = { title: "", artist: "", cover_url: "", duration: "", genre: "", featured: true };

const inputCls = "w-full border border-white/20 bg-black px-3 py-2 font-mono text-sm font-bold text-[#FAFAFA] outline-none focus:border-[#A855F7]";
const labelCls = "font-mono text-sm font-bold font-bold uppercase tracking-[0.22em] text-[#FAFAFA]";

// -- Track Preview helpers -------------------------------------------------
const ytIdFromUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1) || null;
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => p === "embed" || p === "shorts" || p === "live");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
  } catch (_e) { /* noop */ }
  return null;
};

const PreviewModal = ({ preview, onClose }) => {
  // Playlist mode: preview.playlist is an array of { url, title, artist }.
  // Single-track mode: preview has direct { url, title, artist } fields.
  const isPlaylist = Array.isArray(preview?.playlist) && preview.playlist.length > 0;
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [preview]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!preview) return null;

  const current = isPlaylist ? preview.playlist[idx] : preview;
  const total = isPlaylist ? preview.playlist.length : 1;
  const ytId = ytIdFromUrl(current?.url);

  const next = () => setIdx((i) => (i + 1) % total);
  const prev = () => setIdx((i) => (i - 1 + total) % total);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="track-preview-modal"
    >
      <div
        className="w-full max-w-2xl border border-white/10 bg-[#0a0a0a] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-sm font-bold uppercase tracking-[0.24em] text-[#A855F7]">
              {isPlaylist ? `${preview.blockLabel || "Block Preview"} · ${idx + 1} / ${total}` : "Preview"}
            </div>
            <div className="mt-1 truncate font-[Unbounded,sans-serif] text-lg font-bold text-[#FAFAFA]">
              {current?.title || "Untitled Track"}
            </div>
            {current?.artist ? (
              <div className="mt-1 truncate font-mono text-sm font-bold text-[#FAFAFA]">{current.artist}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="track-preview-close"
            aria-label="Close preview"
            className="border border-white/10 p-2 text-[#FAFAFA] hover:bg-white hover:text-black"
          >
            <X size={16} />
          </button>
        </div>

        {ytId ? (
          <div className="relative aspect-video w-full bg-black">
            <iframe
              key={ytId}
              title="track-preview"
              className="h-full w-full"
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              data-testid="track-preview-iframe"
            />
          </div>
        ) : current?.url ? (
          <div className="border border-white/10 p-4">
            <audio
              key={current.url}
              autoPlay
              controls
              src={current.url}
              className="w-full"
              onEnded={() => { if (isPlaylist) next(); }}
              data-testid="track-preview-audio"
            />
            <div className="mt-2 truncate font-mono text-sm font-bold text-[#A1A1AA]">{current.url}</div>
          </div>
        ) : (
          <div className="font-mono text-sm font-bold text-[#FF3B30]">No URL provided.</div>
        )}

        {isPlaylist ? (
          <>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={prev}
                data-testid="track-preview-prev"
                className="flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#FAFAFA] hover:bg-white hover:text-black"
              >
                <SkipBack size={14} /> Prev
              </button>
              <button
                type="button"
                onClick={next}
                data-testid="track-preview-next"
                className="flex items-center gap-2 border border-[#A855F7] px-3 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A855F7] hover:bg-[#A855F7] hover:text-black"
              >
                Next <SkipForward size={14} />
              </button>
              {ytId ? (
                <span className="ml-2 font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
                  YouTube · use Next to advance
                </span>
              ) : (
                <span className="ml-2 font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
                  Auto-advances when track ends
                </span>
              )}
            </div>
            <ol className="mt-3 max-h-40 overflow-y-auto border border-white/10">
              {preview.playlist.map((t, i) => (
                <li
                  key={`${i}-${t.url}`}
                  className={`flex cursor-pointer items-center gap-2 border-b border-white/5 px-3 py-2 text-sm font-mono font-bold last:border-b-0 ${i === idx ? "bg-[#A855F7]/20 text-[#A855F7]" : "text-[#FAFAFA] hover:bg-white/5"}`}
                  onClick={() => setIdx(i)}
                  data-testid={`track-preview-item-${i}`}
                >
                  <span className="w-6 text-xs">{i + 1}.</span>
                  <span className="min-w-0 flex-1 truncate">
                    {t.title || t.url || "Untitled"}
                    {t.artist ? <span className="text-[#A1A1AA]"> — {t.artist}</span> : null}
                  </span>
                </li>
              ))}
            </ol>
          </>
        ) : null}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, checking, logout } = useAuth();
  const [tab, setTab] = useState("shows");
  const [shows, setShows] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [newShow, setNewShow] = useState(emptyShow);
  const [newTrack, setNewTrack] = useState(emptyTrack);
  const [preview, setPreview] = useState(null);
  // Per-block value for the "quick add URL" input (Enter/Add to append as a track).
  const [quickAdd, setQuickAdd] = useState({});
  // Key of the track currently expanded to show/edit title + artist metadata ("b{blockIdx}-t{trackIdx}").
  const [expandedTrack, setExpandedTrack] = useState(null);

  const loadAll = () => {
    api.get("/shows").then((r) => setShows(r.data)).catch(() => toast.error("Failed to load shows"));
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => toast.error("Failed to load tracks"));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Failed to load settings"));
  };

  useEffect(() => { loadAll(); }, []);

  // Close any open preview modal when switching admin tabs.
  useEffect(() => { setPreview(null); }, [tab]);

  if (checking) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const addShow = async () => {
    try {
      await api.post("/shows", newShow);
      setNewShow(emptyShow);
      toast.success("Show added");
      loadAll();
    } catch (e) { toast.error("Failed to add show"); }
  };

  const delShow = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    await api.delete(`/shows/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const addTrack = async () => {
    try {
      await api.post("/tracks", newTrack);
      setNewTrack(emptyTrack);
      toast.success("Track added");
      loadAll();
    } catch (e) { toast.error("Failed to add track"); }
  };

  const delTrack = async (id) => {
    if (!window.confirm("Delete this track?")) return;
    await api.delete(`/tracks/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const saveSettings = async () => {
    await api.put("/settings", settings);
    toast.success("Settings saved");
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-black pb-32 text-[#FAFAFA]" data-testid="admin-dashboard-root">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-5 sm:px-6">
          <div>
            <div className="font-mono text-sm font-bold uppercase tracking-[0.28em] text-[#A855F7]">Studio Console</div>
            <div className="mt-1 font-[Unbounded,sans-serif] text-xl font-bold uppercase tracking-tight">Black Box Admin</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#FAFAFA] sm:inline">{user.email}</span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] hover:bg-white hover:text-black"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        <div className="mx-auto flex max-w-[1400px] gap-6 px-4 sm:px-6">
          {["shows", "tracks", "router", "settings"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`border-b-2 pb-3 pt-2 font-mono text-sm font-bold uppercase tracking-[0.24em] ${
                tab === t ? "border-[#A855F7] text-[#A855F7]" : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6">
        {tab === "shows" && (
          <div>
            <h2 className="font-[Unbounded,sans-serif] text-2xl uppercase tracking-tight">Manage Schedule</h2>

            {/* New show form */}
            <div className="mt-6 border border-white/10 p-6">
              <div className="mb-4 font-mono text-sm font-bold uppercase tracking-[0.24em] text-[#A855F7]">+ New Show</div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                <div><div className={labelCls}>Title</div><input className={inputCls} data-testid="new-show-title" value={newShow.title} onChange={(e)=>setNewShow({...newShow,title:e.target.value})} /></div>
                <div><div className={labelCls}>DJ</div><input className={inputCls} data-testid="new-show-dj" value={newShow.dj} onChange={(e)=>setNewShow({...newShow,dj:e.target.value})} /></div>
                <div>
                  <div className={labelCls}>Day</div>
                  <select className={inputCls} data-testid="new-show-day" value={newShow.day} onChange={(e)=>setNewShow({...newShow,day:e.target.value})}>
                    {DAYS.map((d)=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div><div className={labelCls}>Genre</div><input className={inputCls} data-testid="new-show-genre" value={newShow.genre} onChange={(e)=>setNewShow({...newShow,genre:e.target.value})} /></div>
                <div><div className={labelCls}>Start (HH:MM)</div><input className={inputCls} data-testid="new-show-start" value={newShow.start_time} onChange={(e)=>setNewShow({...newShow,start_time:e.target.value})} /></div>
                <div><div className={labelCls}>End (HH:MM)</div><input className={inputCls} data-testid="new-show-end" value={newShow.end_time} onChange={(e)=>setNewShow({...newShow,end_time:e.target.value})} /></div>
                <div className="sm:col-span-2"><div className={labelCls}>Cover URL</div><input className={inputCls} data-testid="new-show-cover" value={newShow.cover_url} onChange={(e)=>setNewShow({...newShow,cover_url:e.target.value})} /></div>
              </div>
              <button onClick={addShow} data-testid="new-show-submit" className="mt-4 flex items-center gap-2 border border-[#A855F7] bg-[#A855F7] px-4 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] text-black hover:bg-transparent hover:text-[#A855F7]">
                <Plus size={14} /> Add Show
              </button>
            </div>

            {/* Shows table */}
            <div className="mt-8 border border-white/10">
              <div className="grid grid-cols-[80px_1fr_1fr_1fr_1fr_60px] items-center gap-3 border-b border-white/10 bg-black/40 px-4 py-3 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
                <div>Day</div><div>Title</div><div>DJ</div><div>Time</div><div>Genre</div><div></div>
              </div>
              {shows.map((s)=>(
                <div key={s.id} data-testid={`show-row-${s.id}`} className="grid grid-cols-[80px_1fr_1fr_1fr_1fr_60px] items-center gap-3 border-b border-white/5 px-4 py-3 font-mono text-sm font-bold">
                  <div className="text-[#A855F7]">{s.day}</div>
                  <div className="text-[#FAFAFA]">{s.title}</div>
                  <div className="text-[#A1A1AA]">{s.dj}</div>
                  <div className="text-[#A1A1AA]">{s.start_time} — {s.end_time}</div>
                  <div className="text-[#A1A1AA]">{s.genre}</div>
                  <button onClick={()=>delShow(s.id)} data-testid={`delete-show-${s.id}`} className="text-[#FF3B30] hover:text-white"><Trash2 size={14} /></button>
                </div>
              ))}
              {shows.length === 0 ? <div className="p-6 font-mono text-sm font-bold text-[#A1A1AA]">No shows yet.</div> : null}
            </div>
          </div>
        )}

        {tab === "tracks" && (
          <div>
            <h2 className="font-[Unbounded,sans-serif] text-2xl uppercase tracking-tight">Featured Tracks</h2>

            <div className="mt-6 border border-white/10 p-6">
              <div className="mb-4 font-mono text-sm font-bold uppercase tracking-[0.24em] text-[#A855F7]">+ New Track</div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                <div><div className={labelCls}>Title</div><input className={inputCls} data-testid="new-track-title" value={newTrack.title} onChange={(e)=>setNewTrack({...newTrack,title:e.target.value})} /></div>
                <div><div className={labelCls}>Artist</div><input className={inputCls} data-testid="new-track-artist" value={newTrack.artist} onChange={(e)=>setNewTrack({...newTrack,artist:e.target.value})} /></div>
                <div><div className={labelCls}>Duration</div><input className={inputCls} data-testid="new-track-duration" value={newTrack.duration} onChange={(e)=>setNewTrack({...newTrack,duration:e.target.value})} /></div>
                <div><div className={labelCls}>Genre</div><input className={inputCls} data-testid="new-track-genre" value={newTrack.genre} onChange={(e)=>setNewTrack({...newTrack,genre:e.target.value})} /></div>
                <div className="sm:col-span-2">
                  <div className={labelCls}>Cover Image</div>
                  <div className="flex items-center gap-3">
                    {newTrack.cover_url ? (
                      <img
                        src={newTrack.cover_url}
                        alt=""
                        data-testid="new-track-cover-preview"
                        className="h-14 w-14 flex-shrink-0 border border-white/10 bg-black object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center border border-dashed border-white/20 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">
                        None
                      </div>
                    )}
                    <input
                      className={`${inputCls} flex-1`}
                      data-testid="new-track-cover"
                      placeholder="Paste URL or upload →"
                      value={newTrack.cover_url}
                      onChange={(e) => setNewTrack({ ...newTrack, cover_url: e.target.value })}
                    />
                    <ImageUploadButton
                      testid="new-track-cover-upload"
                      onUploaded={(url) => setNewTrack({ ...newTrack, cover_url: url })}
                    />
                    {newTrack.cover_url ? (
                      <button
                        type="button"
                        onClick={() => setNewTrack({ ...newTrack, cover_url: "" })}
                        data-testid="new-track-cover-clear"
                        className="border border-white/20 p-2 text-[#A1A1AA] hover:bg-white hover:text-black"
                        aria-label="Clear cover"
                      >
                        <X size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>
                <label className="flex items-center gap-2 pt-6 font-mono text-sm font-bold text-[#FAFAFA]">
                  <input type="checkbox" data-testid="new-track-featured" checked={newTrack.featured} onChange={(e)=>setNewTrack({...newTrack,featured:e.target.checked})} />
                  Featured
                </label>
              </div>
              <button onClick={addTrack} data-testid="new-track-submit" className="mt-4 flex items-center gap-2 border border-[#A855F7] bg-[#A855F7] px-4 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] text-black hover:bg-transparent hover:text-[#A855F7]">
                <Plus size={14} /> Add Track
              </button>
            </div>

            <div className="mt-8 border border-white/10">
              <div className="grid grid-cols-[56px_1fr_1fr_100px_100px_80px_60px] items-center gap-3 border-b border-white/10 bg-black/40 px-4 py-3 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
                <div>Cover</div><div>Title</div><div>Artist</div><div>Genre</div><div>Duration</div><div>Featured</div><div></div>
              </div>
              {tracks.map((t)=>(
                <div key={t.id} data-testid={`track-row-${t.id}`} className="grid grid-cols-[56px_1fr_1fr_100px_100px_80px_60px] items-center gap-3 border-b border-white/5 px-4 py-3 font-mono text-sm font-bold">
                  {t.cover_url ? (
                    <img src={t.cover_url} alt="" className="h-10 w-10 border border-white/10 bg-black object-cover" data-testid={`track-cover-thumb-${t.id}`} />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center border border-dashed border-white/20 text-[#52525B]"><Play size={12} /></div>
                  )}
                  <div className="truncate text-[#FAFAFA]">{t.title}</div>
                  <div className="truncate text-[#A1A1AA]">{t.artist}</div>
                  <div className="truncate text-[#A1A1AA]">{t.genre}</div>
                  <div className="text-[#A1A1AA]">{t.duration}</div>
                  <div className="text-[#A855F7]">{t.featured ? "Yes" : "No"}</div>
                  <button onClick={()=>delTrack(t.id)} data-testid={`delete-track-${t.id}`} className="text-[#FF3B30] hover:text-white"><Trash2 size={14} /></button>
                </div>
              ))}
              {tracks.length === 0 ? <div className="p-6 font-mono text-sm font-bold text-[#A1A1AA]">No tracks yet.</div> : null}
            </div>
          </div>
        )}

        {tab === "router" && settings && (
          <AdminTrackRouter settings={settings} setSettings={setSettings} />
        )}

        {tab === "settings" && settings && (
          <div>
            <h2 className="font-[Unbounded,sans-serif] text-2xl uppercase tracking-tight">Station Settings</h2>
            <div className="mt-6 grid gap-6 border border-white/10 p-6 sm:grid-cols-2">
              <div className="sm:col-span-2"><div className={labelCls}>About Title</div><input className={inputCls} data-testid="settings-about-title" value={settings.about_title} onChange={(e)=>setSettings({...settings,about_title:e.target.value})} /></div>
              <div className="sm:col-span-2"><div className={labelCls}>About Body</div><textarea rows={4} className={inputCls} data-testid="settings-about-body" value={settings.about_body} onChange={(e)=>setSettings({...settings,about_body:e.target.value})} /></div>
              <div><div className={labelCls}>Contact Email</div><input className={inputCls} data-testid="settings-contact-email" value={settings.contact_email} onChange={(e)=>setSettings({...settings,contact_email:e.target.value})} /></div>
              <div>
                <div className={labelCls}>Stream URL</div>
                <div className="flex items-center gap-2">
                  <input className={inputCls} data-testid="settings-stream-url" value={settings.stream_url} onChange={(e)=>setSettings({...settings,stream_url:e.target.value})} />
                  <AudioUploadButton testid="settings-stream-url-upload" label="" onUploaded={(url) => setSettings({...settings, stream_url: url})} />
                </div>
              </div>
              <div><div className={labelCls}>Stream Metadata URL</div><input className={inputCls} data-testid="settings-metadata-url" value={settings.stream_metadata_url} onChange={(e)=>setSettings({...settings,stream_metadata_url:e.target.value})} /></div>
              <div><div className={labelCls}>Instagram URL</div><input className={inputCls} data-testid="settings-instagram" value={settings.social_instagram} onChange={(e)=>setSettings({...settings,social_instagram:e.target.value})} /></div>
              <div><div className={labelCls}>Twitter URL</div><input className={inputCls} data-testid="settings-twitter" value={settings.social_twitter} onChange={(e)=>setSettings({...settings,social_twitter:e.target.value})} /></div>
              <div><div className={labelCls}>Soundcloud URL</div><input className={inputCls} data-testid="settings-soundcloud" value={settings.social_soundcloud} onChange={(e)=>setSettings({...settings,social_soundcloud:e.target.value})} /></div>
              <div><div className={labelCls}>Mixcloud URL</div><input className={inputCls} data-testid="settings-mixcloud" value={settings.social_mixcloud} onChange={(e)=>setSettings({...settings,social_mixcloud:e.target.value})} /></div>
            </div>

            <h3 className="mt-10 font-[Unbounded,sans-serif] text-xl font-bold uppercase tracking-tight">Healing Frequency Links</h3>
            <p className="mt-2 max-w-2xl font-mono text-sm font-bold text-[#FAFAFA]">
              Paste a YouTube link or a direct audio URL for each frequency. Between 12:00 AM and 5:30 AM the
              player will auto-switch to the listener&apos;s chosen frequency (or the nightly default if they haven&apos;t picked one).
            </p>
            <div className="mt-6 grid gap-6 border border-white/10 p-6 sm:grid-cols-2">
              {[
                {hz: "174", meaning: "Grounding & Pain Relief"},
                {hz: "396", meaning: "Release Fear & Guilt"},
                {hz: "417", meaning: "Facilitate Change"},
                {hz: "432", meaning: "Universal Harmony / Natural Tuning"},
                {hz: "528", meaning: "DNA Repair / Love"},
                {hz: "639", meaning: "Connection & Relationships"},
                {hz: "741", meaning: "Expression & Solutions"},
                {hz: "963", meaning: "Awakening & Unity"},
                {hz: "1111", meaning: "Angelic Frequency / Manifestation"},
              ].map((f) => (
                <div key={f.hz}>
                  <div className={labelCls}>{f.hz} Hz · {f.meaning}</div>
                  <div className="flex items-center gap-2">
                    <input
                      className={inputCls}
                      placeholder="Paste URL or upload a file →"
                      data-testid={`settings-healing-${f.hz}`}
                      value={(settings.healing_links && settings.healing_links[f.hz]) || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          healing_links: { ...(settings.healing_links || {}), [f.hz]: e.target.value },
                        })
                      }
                    />
                    <AudioUploadButton
                      testid={`settings-healing-${f.hz}-upload`}
                      label=""
                      onUploaded={(url) => setSettings({
                        ...settings,
                        healing_links: { ...(settings.healing_links || {}), [f.hz]: url },
                      })}
                    />
                  </div>
                </div>
              ))}
            </div>

            <h3 className="mt-10 font-[Unbounded,sans-serif] text-xl font-bold uppercase tracking-tight">
              Black Box Music — Daily Programming
            </h3>
            <p className="mt-2 max-w-2xl font-mono text-sm font-bold text-[#FAFAFA]">
              8 blocks of 2 hours from <span className="text-[#A855F7]">07:00 to 23:00 ET</span> every day.
              Each hour plays 55 min of music from that block, followed by a 5-min ad break. Track order is
              re-shuffled each week. Paste any YouTube or direct-audio URLs — add as many per block as you like.
            </p>

            <div className="mt-4 grid gap-4 border border-white/10 p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-3 font-mono text-sm font-bold text-[#FAFAFA]">
                  <input
                    type="checkbox"
                    data-testid="settings-deep-enabled"
                    checked={!!(settings.deep_corners && settings.deep_corners.enabled)}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        deep_corners: {
                          ...(settings.deep_corners || { intermission_url: "", blocks: [] }),
                          enabled: e.target.checked,
                        },
                      })
                    }
                  />
                  Black Box Music auto-programming enabled
                </label>
                <div>
                  <div className={labelCls}>Intermission URL (5-min ad reel)</div>
                  <div className="flex items-center gap-2">
                    <input
                      className={inputCls}
                      placeholder="Paste URL or upload a file →"
                      data-testid="settings-deep-intermission"
                      value={(settings.deep_corners && settings.deep_corners.intermission_url) || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          deep_corners: {
                            ...(settings.deep_corners || { enabled: true, blocks: [] }),
                            intermission_url: e.target.value,
                          },
                        })
                      }
                    />
                    <AudioUploadButton
                      testid="settings-deep-intermission-upload"
                      label=""
                      onUploaded={(url) =>
                        setSettings({
                          ...settings,
                          deep_corners: {
                            ...(settings.deep_corners || { enabled: true, blocks: [] }),
                            intermission_url: url,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {Array.from({ length: 8 }, (_, blockIdx) => {
                const startHour = 7 + blockIdx * 2;
                const endHour = startHour + 2;
                const timeLabel = `${String(startHour).padStart(2, "0")}:00 – ${String(endHour).padStart(2, "0")}:00`;
                const dc = settings.deep_corners || { enabled: true, intermission_url: "", blocks: [] };
                const blocks = Array.isArray(dc.blocks) ? dc.blocks : [];
                // Normalize legacy shape: array of URLs OR object {name, tracks}.
                const raw = blocks[blockIdx];
                const block = Array.isArray(raw)
                  ? { name: "", synopsis: "", tracks: raw }
                  : raw && typeof raw === "object"
                  ? { name: raw.name || "", synopsis: raw.synopsis || "", tracks: Array.isArray(raw.tracks) ? raw.tracks : [] }
                  : { name: "", synopsis: "", tracks: [] };

                const commit = (nextBlock) => {
                  const newBlocks = Array.from({ length: 8 }, (_, i) => {
                    if (i === blockIdx) return nextBlock;
                    const existing = blocks[i];
                    if (Array.isArray(existing)) return { name: "", synopsis: "", tracks: existing };
                    if (existing && typeof existing === "object") {
                      return {
                        name: existing.name || "",
                        synopsis: existing.synopsis || "",
                        tracks: Array.isArray(existing.tracks) ? existing.tracks : [],
                      };
                    }
                    return { name: "", synopsis: "", tracks: [] };
                  });
                  setSettings({ ...settings, deep_corners: { ...dc, blocks: newBlocks } });
                };

                return (
                  <div key={blockIdx} className="border-t border-white/10 pt-4" data-testid={`deep-block-${blockIdx}`}>
                    <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#A855F7]">
                          Block {blockIdx + 1} · {timeLabel}
                        </span>
                        <input
                          className={`${inputCls} max-w-md`}
                          placeholder={`Block ${blockIdx + 1} name (e.g. "Morning Warmup")`}
                          data-testid={`deep-block-${blockIdx}-name`}
                          value={block.name}
                          onChange={(e) => commit({ ...block, name: e.target.value })}
                        />
                        <textarea
                          className={`${inputCls} max-w-md`}
                          rows={2}
                          placeholder="Short synopsis (1-2 lines shown on the homepage)"
                          data-testid={`deep-block-${blockIdx}-synopsis`}
                          value={block.synopsis}
                          onChange={(e) => commit({ ...block, synopsis: e.target.value })}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const playable = block.tracks
                              .map((raw) =>
                                typeof raw === "string"
                                  ? { url: raw, title: "", artist: "" }
                                  : { url: raw?.url || "", title: raw?.title || "", artist: raw?.artist || "" }
                              )
                              .filter((t) => t.url && t.url.trim().length > 0);
                            if (playable.length === 0) {
                              toast.error("No playable tracks in this block yet");
                              return;
                            }
                            // Fisher-Yates shuffle for a randomized preview (independent of the weekly seed).
                            const shuffled = playable.slice();
                            for (let i = shuffled.length - 1; i > 0; i--) {
                              const j = Math.floor(Math.random() * (i + 1));
                              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                            }
                            setPreview({
                              playlist: shuffled,
                              blockLabel: block.name ? `Block ${blockIdx + 1} · ${block.name}` : `Block ${blockIdx + 1}`,
                            });
                          }}
                          disabled={block.tracks.length === 0}
                          data-testid={`deep-block-${blockIdx}-preview-all`}
                          className="flex items-center gap-2 border border-[#A855F7] px-3 py-1.5 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#A855F7] hover:bg-[#A855F7] hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#A855F7]"
                          title="Play this block's tracks in shuffled order"
                        >
                          <Shuffle size={12} strokeWidth={2.5} /> Preview Block
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedTrack(null)}
                          data-testid={`deep-block-${blockIdx}-collapse-all`}
                          className="border border-white/20 px-3 py-1.5 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#A1A1AA] hover:bg-white hover:text-black"
                        >
                          {block.tracks.length} track{block.tracks.length === 1 ? "" : "s"}
                        </button>
                      </div>
                    </div>

                    {/* Compact track list */}
                    {block.tracks.length === 0 ? (
                      <div className="font-mono text-sm font-bold text-[#A1A1AA]">
                        No tracks yet — paste a URL below to add one.
                      </div>
                    ) : (
                      <ul className="space-y-1">
                        {block.tracks.map((raw, i) => {
                          const t =
                            typeof raw === "string"
                              ? { url: raw, title: "", artist: "" }
                              : {
                                  url: raw?.url || "",
                                  title: raw?.title || "",
                                  artist: raw?.artist || "",
                                };
                          const rowKey = `b${blockIdx}-t${i}`;
                          const isExpanded = expandedTrack === rowKey;
                          const updateTrack = (patch) => {
                            const next = [...block.tracks];
                            next[i] = { ...t, ...patch };
                            commit({ ...block, tracks: next });
                          };
                          const removeTrack = () => {
                            if (expandedTrack === rowKey) setExpandedTrack(null);
                            commit({ ...block, tracks: block.tracks.filter((_, j) => j !== i) });
                          };
                          return (
                            <li key={`${rowKey}-${t.url || t.title || "empty"}`} className="border border-white/5">
                              <div className="flex items-center gap-2 px-3 py-2">
                                <span className="w-6 font-mono text-xs font-bold text-[#A855F7]">{i + 1}.</span>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-mono text-sm text-[#FAFAFA]">
                                    {t.url || <span className="text-[#FF3B30]">(no URL)</span>}
                                  </div>
                                  {(t.title || t.artist) && !isExpanded ? (
                                    <div className="truncate font-mono text-xs font-bold text-[#A1A1AA]">
                                      {t.title}{t.artist ? ` — ${t.artist}` : ""}
                                    </div>
                                  ) : null}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setPreview({ url: t.url, title: t.title, artist: t.artist })}
                                  disabled={!t.url}
                                  data-testid={`deep-block-${blockIdx}-preview-${i}`}
                                  className="border border-[#A855F7] p-2 text-[#A855F7] hover:bg-[#A855F7] hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#A855F7]"
                                  aria-label="Preview track"
                                >
                                  <Play size={12} strokeWidth={2.5} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setExpandedTrack(isExpanded ? null : rowKey)}
                                  data-testid={`deep-block-${blockIdx}-edit-${i}`}
                                  className={`border border-white/20 p-2 text-[#FAFAFA] hover:bg-white hover:text-black ${isExpanded ? "bg-white text-black" : ""}`}
                                  aria-label="Edit title and artist"
                                  title="Edit title/artist"
                                >
                                  <ChevronDown size={14} className={isExpanded ? "rotate-180 transition-transform" : "transition-transform"} />
                                </button>
                                <button
                                  type="button"
                                  onClick={removeTrack}
                                  data-testid={`deep-block-${blockIdx}-remove-${i}`}
                                  className="border border-white/10 p-2 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black"
                                  aria-label="Remove track"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              {isExpanded ? (
                                <div className="grid grid-cols-1 gap-2 border-t border-white/5 bg-black/40 p-3 md:grid-cols-[1fr_1fr_2fr_auto]">
                                  <input
                                    className={inputCls}
                                    placeholder="Track title (optional)"
                                    data-testid={`deep-block-${blockIdx}-track-${i}-title`}
                                    value={t.title}
                                    onChange={(e) => updateTrack({ title: e.target.value })}
                                  />
                                  <input
                                    className={inputCls}
                                    placeholder="Artist (optional)"
                                    data-testid={`deep-block-${blockIdx}-track-${i}-artist`}
                                    value={t.artist}
                                    onChange={(e) => updateTrack({ artist: e.target.value })}
                                  />
                                  <input
                                    className={inputCls}
                                    placeholder="URL"
                                    data-testid={`deep-block-${blockIdx}-track-${i}`}
                                    value={t.url}
                                    onChange={(e) => updateTrack({ url: e.target.value })}
                                  />
                                  <AudioUploadButton
                                    testid={`deep-block-${blockIdx}-track-${i}-upload`}
                                    label=""
                                    onUploaded={(url) => updateTrack({ url })}
                                  />
                                </div>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/* Quick-add URL box */}
                    {(() => {
                      const qaKey = `qa-${blockIdx}`;
                      const value = quickAdd[qaKey] || "";
                      const submit = () => {
                        const url = value.trim();
                        if (!url) return;
                        commit({ ...block, tracks: [...block.tracks, { url, title: "", artist: "" }] });
                        setQuickAdd((prev) => ({ ...prev, [qaKey]: "" }));
                      };
                      return (
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            className={inputCls}
                            placeholder="Paste YouTube URL or audio URL and press Enter"
                            data-testid={`deep-block-${blockIdx}-quick-add-input`}
                            value={value}
                            onChange={(e) => setQuickAdd((prev) => ({ ...prev, [qaKey]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
                          />
                          <button
                            type="button"
                            onClick={submit}
                            disabled={!value.trim()}
                            data-testid={`deep-block-${blockIdx}-quick-add-btn`}
                            className="flex items-center gap-2 border border-[#A855F7] px-4 py-2 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#A855F7] transition-colors hover:bg-[#A855F7] hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#A855F7]"
                          >
                            <Plus size={14} /> Add
                          </button>
                          <AudioUploadButton
                            testid={`deep-block-${blockIdx}-upload-track`}
                            label="Upload"
                            onUploaded={(url) => {
                              if (!url) return;
                              commit({ ...block, tracks: [...block.tracks, { url, title: "", artist: "" }] });
                            }}
                          />
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            <button onClick={saveSettings} data-testid="settings-save" className="mt-6 flex items-center gap-2 border border-[#A855F7] bg-[#A855F7] px-4 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] text-black hover:bg-transparent hover:text-[#A855F7]">
              <Save size={14} /> Save Settings
            </button>
          </div>
        )}
      </main>

      <PreviewModal preview={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

export default AdminDashboard;
